import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import {
  Avatar,
  Heading,
  Paragraph,
  PlatformInstagramColorIcon,
  PlatformSnapchatColorIcon,
  PlatformTikTokColorIcon,
  PlatformYouTubeColorIcon,
  TrashIcon,
  colors,
} from 'ui-components';
import { IconButton } from 'ui-components/dist/Button';
import { PlatformData } from '~/hooks/api/useOPCreatorSearch';
import { SmartAccountsType } from '~/hooks/api/useSearchSmartProfiles';
import { useAddTalentStore } from '~/screens/AddTalent';
import { formatAbbr } from '~/utils';

export const PlatformInfoCard = ({
  platform,
  platformData,
}: {
  platform?: string;
  platformData?: SmartAccountsType | PlatformData;
}) => {
  const {
    smartProfileEnabled,
    platforms,
    setPlatforms,
    setSearchResult,
    setSearchedPlatform,
  } = useAddTalentStore();

  const platformIcon = useMemo(
    () =>
      match(platform)
        .with('instagram', () => <PlatformInstagramColorIcon boxSize={6} />)
        .with('tiktok', () => <PlatformTikTokColorIcon boxSize={6} />)
        .with('youtube', () => <PlatformYouTubeColorIcon boxSize={6} />)
        .with('snapchat', () => <PlatformSnapchatColorIcon boxSize={6} />)
        .otherwise(() => undefined),
    [platform],
  );

  const handleRemove = () => {
    const index = platforms.findIndex(
      (p) => p.handle === platformData?.handle && p.platform === platform,
    );
    platforms.splice(index, 1);
    setPlatforms(platforms);

    if (platformData?.type === 'search-result') {
      setSearchResult(undefined);
      setSearchedPlatform(undefined);
    }
  };

  return (
    <Flex gap={6} alignItems="center" justifyContent="space-between">
      <Flex flex="1" gap={6}>
        <Avatar
          alt=""
          icon={platformIcon}
          size="xl"
          src={platformData?.profilePictureUrl}
        />
        <Grid gap={4}>
          <Box>
            <Heading size="sm">@{platformData?.handle}</Heading>
            <Paragraph size="md">{platformData?.name}</Paragraph>
          </Box>
          <Box>
            <Text fontSize="0.75rem" color={colors.primary[300]}>
              {platform?.match('youtube') ? 'SUBSCRIBERS' : 'FOLLOWERS'}
            </Text>
            <Text fontWeight="medium" fontSize="1rem">
              {formatAbbr(platformData?.followersCount) || '-'}
            </Text>
          </Box>
        </Grid>
      </Flex>
      {smartProfileEnabled && platforms?.length > 1 ? (
        <IconButton
          aria-label="Remove Platform"
          size="md"
          variant="tertiary"
          onClick={handleRemove}
          icon={<TrashIcon boxSize={4} />}
          trackId="foam-add-talent-platform-info-remove"
        />
      ) : null}
    </Flex>
  );
};
